import { create } from 'zustand';
import { api, ApiClientError, setAuthToken, getAuthToken, setUnauthorizedHandler, type UnauthorizedCode } from '@/core/api/apiClient';
import { disconnectSocket, setSessionEndedHandler } from '@/core/realtime/socket';
import { resetRouter } from '@/core/router/router';
import { useToastStore } from '@/core/ui/toastStore';
import { clearLastVisitSticky } from '@/features/task-detail/components/TaskComments';
import { useTasksStore } from '@/features/tasks/store/tasksStore';
import { useAreaStore } from '@/features/areas/store/areaStore';
import { useLookupStore } from '@/features/lookups/store/lookupStore';
import { useTaskDetailStore } from '@/features/task-detail/store/taskDetailStore';
import { useDashboardStore } from '@/features/dashboard/store/dashboardStore';
import type { AuthOptions, AuthUser, LoginResponse, MeResponse } from '@cg-projects/shared';

// ONE LOGIN (v1.94.0): the address someone was trying to open when they landed on
// the sign-in screen (a task link from an email, a bookmark). They leave for CG
// Team to sign in, and the Team tile brings them back in a NEW tab with only a
// pass in the address — so the destination is kept here, on this device, and
// used once. Fifteen minutes, then it is stale and ignored.
const RETURN_KEY = 'cgp:after-team';
const RETURN_TTL_MS = 15 * 60 * 1000;
export function rememberReturnTo(hash: string): void {
  try {
    if (!/^#\/./.test(hash) || /^#\/sso\//.test(hash)) { localStorage.removeItem(RETURN_KEY); return; }
    localStorage.setItem(RETURN_KEY, JSON.stringify({ hash, at: Date.now() }));
  } catch { /* private mode: they land on the home page instead */ }
}
export function takeReturnTo(): string | null {
  try {
    const raw = localStorage.getItem(RETURN_KEY);
    localStorage.removeItem(RETURN_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw) as { hash?: unknown; at?: unknown };
    if (typeof v.hash !== 'string' || typeof v.at !== 'number' || Date.now() - v.at > RETURN_TTL_MS) return null;
    return /^#\/./.test(v.hash) && !/^#\/sso\//.test(v.hash) ? v.hash : null;
  } catch { return null; }
}

interface AuthState {
  user: AuthUser | null;
  booting: boolean;
  loggingIn: boolean;
  error: string | null;
  /** How this instance signs people in (GET /auth/options). null until known. */
  options: AuthOptions | null;
  loadOptions: () => Promise<void>;
  restore: () => Promise<void>;
  login: (email: string, password: string) => Promise<boolean>;
  /** Spend a one-time hand-off pass from CG Team for a real session. */
  ssoExchange: (token: string) => Promise<boolean>;
  logout: () => void;
}

export const useAuthStore = create<AuthState>()((set) => ({
  user: null,
  booting: true,
  loggingIn: false,
  error: null,
  options: null,

  // Unknown (offline, or an old server) = the one-login screen: the password
  // door is shut on the server either way, so offering a password box that can
  // only fail would be the worse guess.
  loadOptions: async () => {
    try {
      const options = await api.get<AuthOptions>('/auth/options');
      set({ options });
    } catch {
      set({ options: { passwordSignIn: false, teamUrl: 'https://team.chronicguru.com/' } });
    }
  },

  restore: async () => {
    if (!getAuthToken()) {
      set({ booting: false });
      return;
    }
    try {
      const { user } = await api.get<MeResponse>('/auth/me');
      set({ user, booting: false });
    } catch (e) {
      // Only a REAL "who are you" answer invalidates the token. An offline
      // boot (status 0/timeout — reachable now that the shell opens from
      // cache, v1.84.0) must keep the session: nuking it would strand the
      // person at a login screen they can't use until they find wifi.
      const status = (e as { status?: number }).status ?? 0;
      if (status === 401 || status === 403) {
        try { void caches?.delete?.('cgp-api-v1'); } catch { /* broom */ }
        setAuthToken(null);
        set({ user: null, booting: false });
      } else {
        set({ user: null, booting: false });
      }
    }
  },

  login: async (email, password) => {
    set({ loggingIn: true, error: null });
    try {
      const result = await api.post<LoginResponse>('/auth/login', { email, password });
      // Session-entry broom (v1.84.0): the offline API copy belongs to
      // whoever was here before — a fresh sign-in must never inherit it,
      // regardless of HOW the previous session ended.
      try { void caches?.delete?.('cgp-api-v1'); } catch { /* best-effort */ }
      setAuthToken(result.token);
      set({ user: result.user, loggingIn: false, error: null });
      return true;
    } catch (err) {
      const message =
        err instanceof ApiClientError ? err.message : 'Sign-in failed. Please try again.';
      set({ loggingIn: false, error: message });
      return false;
    }
  },

  // Arriving from the CG Team tile. Failure here is NOT fatal — it falls through
  // to the ordinary login screen, but with `error` set so the person is told why
  // rather than being dropped at a password box for no visible reason.
  ssoExchange: async (token) => {
    try {
      const result = await api.post<LoginResponse>('/auth/sso', { token });
      try { void caches?.delete?.('cgp-api-v1'); } catch { /* session-entry broom */ }
      setAuthToken(result.token);
      set({ user: result.user, booting: false, error: null });
      return true;
    } catch (err) {
      const message =
        err instanceof ApiClientError ? err.message : 'Sign-in from CG Team failed. Open Projects from CG Team again.';
      set({ error: message });
      return false;
    }
  },

  logout: () => {
    disconnectSocket();
    setAuthToken(null);
    set({ user: null });
    clearAllUserData();
  },
}));

/** Signing out must leave NOTHING of the previous person behind. These stores
 *  are module singletons: on a shared tablet the next sign-in would otherwise
 *  render the previous user's projects, tasks and open task panel — data they
 *  may have no access to — until each store happened to refetch. Also drops
 *  cached description/comment drafts, which can contain private text. */
function clearAllUserData(): void {
  // The cooler broom (v1.84.0): the offline API copy is the previous
  // person's data — drop it on EVERY sign-out path, including the 401
  // auto-logout. Page-side caches.delete needs no SW round-trip (the
  // controller can be null right after a hard refresh). The OUTBOX is NOT
  // cleared: queued notes are uid-stamped, invisible to the next person,
  // and waiting for their author's next session.
  try { void caches?.delete?.('cgp-api-v1'); } catch { /* best-effort */ }
  try {
    useTasksStore.setState({ tasksByArea: {}, loadingArea: {}, errorByArea: {}, loadedClosed: {}, loadedAt: {}, loadSeq: {}, liveAt: {}, selectedIds: [] });
    useAreaStore.setState({ areas: [], selectedAreaId: null, stagesByArea: {}, stagesLoading: {}, loading: false, areasLoaded: false, error: null });
    useLookupStore.setState({ assignees: [], contentTags: [], standardTags: [], loaded: false, loading: false });
    useTaskDetailStore.setState({ openId: null, task: null, loading: false, error: null, dirty: false });
    useDashboardStore.setState({ data: null, loading: false, error: null });
  } catch { /* never block sign-out */ }
  // Drop the previous user's ROUTE too. Without this, signing in on a shared
  // tablet replays their project + open task from the URL before any fetch.
  try { resetRouter(); } catch { /* never block sign-out */ }
  // A signed-out shared device must stop buzzing with the previous person's
  // message previews (reviewer catch): drop THIS device's push subscription.
  try {
    if ('serviceWorker' in navigator) {
      void navigator.serviceWorker.ready
        .then((reg) => reg.pushManager.getSubscription())
        .then((sub) => {
          if (!sub) return;
          void api.post('/auth/push-unsubscribe', { endpoint: sub.endpoint }).catch(() => undefined);
          void sub.unsubscribe().catch(() => undefined);
        })
        .catch(() => undefined);
    }
  } catch { /* never block sign-out */ }
  // The "new since your last visit" stamp is per-PERSON — user B must not
  // inherit A's divider timing on a shared machine.
  try { clearLastVisitSticky(); } catch { /* never block sign-out */ }
  try {
    for (const k of Object.keys(localStorage)) {
      if (k.startsWith('cgp:draft:')) localStorage.removeItem(k);
    }
  } catch { /* ignore */ }
}

// Expired/revoked session (401 from any request) → drop to the login screen
// with a clear message, instead of a dead app that still looks signed-in.
function endSession(code: UnauthorizedCode): void {
  if (!useAuthStore.getState().user) return; // already logged out
  useAuthStore.getState().logout();
  // Revoked ≠ expired. Telling someone whose access was ended to "sign in again"
  // sends them round a loop against a door that is now shut on purpose.
  const msg = code === 'AUTH_SESSION_REVOKED'
    ? 'Your access to CG Projects has ended. If that looks wrong, ask Pat or Allyson.'
    : 'Your session expired — please sign in again.';
  useToastStore.getState().show(msg, 'error');
}
setUnauthorizedHandler(endSession);
// The socket can notice first (the server drops it the instant a revoke lands),
// and it must look identical to the REST path rather than a silent dead tab.
setSessionEndedHandler(() => endSession('AUTH_SESSION_REVOKED'));
